import { useCallback, useContext, useState } from 'react';
import { Modal } from '@mui/material';
import GlobalContext from 'contexts/GlobalContext';
import { useAppSelector } from 'hooks';

import { CashierContext } from 'components/Cashier/CashierContext';
import AccountMenuPanel from 'components/Profile/AccountMenuPanel';
import MediaImage from 'components/SaloonLobby/MediaImage/MediaImage';
import AvatarFrame from 'components/Streaks/AvatarFrame';
import { useOwnStreakFrame } from 'components/Streaks/state/useSeatStreakFrame';

import styles from './InvalidLocationModal.module.scss';

// Canonical location-help URL. Server-side pushed help pages are per-device
// (iOS / Android / Web), but a single Support entry point is close enough for
// this modal — the article there routes further by user agent.
const LOCATION_HELP_URL =
  'https://help.hijackpoker.com/kb-search-results?q=Location&type=KNOWLEDGE_ARTICLE&groupId=202493791669';

const formatCurrency = (value: number): string => {
  const n = Number(value) || 0;
  if (Number.isInteger(n)) return `$${n.toLocaleString()}`;
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const InvalidLocationModal = () => {
  const { isLocationInvalid, invalidLocationMsg } = useContext(GlobalContext);
  const { openCashier } = useContext(CashierContext);
  const cashBalance = useAppSelector((state) => state.user.playerBankAmount);
  // EN-11380: the same streak frame as the lobby header's avatar.
  const streakFrame = useOwnStreakFrame();
  const promoBalance = useAppSelector((state) => state.user.playerChargeBalance);
  const avatar = useAppSelector((state) => state.user.playerAvatar);

  // Profile side panel is toggled locally, mirroring SaloonHeader's usage of
  // AccountMenuPanel so the "Manage your profile" flow stays in-app instead of
  // redirecting out to ACCOUNT_URL (which was leaving Capacitor / mobile
  // players stranded outside the WebView).
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const handleOpenProfile = useCallback(() => setShowProfileMenu(true), []);
  const handleCloseProfile = useCallback(() => setShowProfileMenu(false), []);
  const handleOpenCashier = useCallback(() => openCashier(), [openCashier]);

  // Keep the modal open whenever the location gate is active — even while a
  // side panel is layered on top. The z-index override below (`sx={{ zIndex
  // 900 }}` on the Modal) demotes it BELOW AccountMenuPanel's ~950 drawer,
  // so the panel renders on top of the modal instead of the lobby. Previously
  // this was gated on `!showProfileMenu && !cashierOpen`, which unmounted the
  // modal and let the lobby show through around the drawer — the exact
  // "ghost lobby behind the panel" symptom.
  const modalOpen = isLocationInvalid;

  const openHelp = () => {
    window.open(LOCATION_HELP_URL, '_blank', 'noopener,noreferrer');
  };

  // The reference design uses a single friendly canonical message rather than
  // the raw Geolocation-API error strings ("The attempt timed out…" etc). The
  // technical message stays available via invalidLocationMsg for logging /
  // support flows, but is intentionally not surfaced to the player here.
  const body =
    invalidLocationMsg && invalidLocationMsg.length > 0
      ? invalidLocationMsg
      : 'We cannot confirm your location is in an approved region. Please make sure Hijack Poker can access your location. For assistance on how to enable your location please go to the article related to your device.';

  return (
    <>
      <Modal
        open={modalOpen}
        disableEscapeKeyDown
        // Demote below AccountMenuPanel (~950) and CashierPanel so those
        // side drawers render ON TOP of the location warning rather than
        // behind it. The location content stays visible around the drawer
        // (Hijack logo, "we cannot confirm your location" copy, Help,
        // MANAGE YOUR PROFILE / GO TO CASHIER buttons) — matching the
        // requested behaviour of "panel over modal, no lobby leaking".
        sx={{ zIndex: 900 }}
        aria-labelledby='invalid-location-title'
        aria-describedby='invalid-location-body'>
        <div className={styles.backdrop}>
          <div className={styles.panel} role='dialog'>
            <div className={styles.topRow}>
              <button
                type='button'
                className={styles.balancePill}
                onClick={handleOpenCashier}
                aria-label='Open cashier'>
                <span className={styles.balanceText}>
                  <span className={styles.cashRow}>
                    <span className={styles.balanceLabel}>Cash:</span>
                    {formatCurrency(cashBalance)}
                  </span>
                  <span className={styles.promoRow}>
                    <span className={styles.balanceLabel}>Promo:</span>
                    {formatCurrency(promoBalance)}
                  </span>
                </span>
              </button>

              <AvatarFrame tier={streakFrame} size={40}>
                <button
                  type='button'
                  className={`${styles.avatarButton} ${streakFrame ? styles.avatarFramed : ''}`}
                  onClick={handleOpenProfile}
                  aria-label='Open profile'>
                  {avatar ? (
                    <MediaImage
                      path={`images/${avatar}`}
                      alt='Player avatar'
                      className={styles.avatarImage}
                    />
                  ) : (
                    // Fallback initial mark when the player has no avatar set yet.
                    // Same neutral bone background as SaloonHeader's avatar slot.
                    <span className={styles.avatarPlaceholder} aria-hidden />
                  )}
                </button>
              </AvatarFrame>
            </div>

            <div className={styles.branding}>
              <div className={styles.hijackTitle} id='invalid-location-title'>
                HIJACK
              </div>
              <div className={styles.pokerSubtitle}>
                <span className={styles.arrow} aria-hidden>
                  ◄
                </span>
                POKER
                <span className={styles.arrow} aria-hidden>
                  ►
                </span>
              </div>
            </div>

            <div className={styles.dashedDivider} aria-hidden />

            <p className={styles.message} id='invalid-location-body'>
              {body}
            </p>

            <p className={styles.instruction}>
              CLICK THE LINK BELOW ON HOW TO ENABLE THE LOCATION SERVICES OF YOUR DEVICE
            </p>

            <button type='button' className={styles.helpLink} onClick={openHelp}>
              Help
            </button>

            <div className={styles.actions}>
              <div className={styles.youCanStill}>YOU CAN STILL</div>
              <button type='button' className={styles.ctaButton} onClick={handleOpenProfile}>
                MANAGE YOUR PROFILE
              </button>
              <div className={styles.orDivider}>OR</div>
              <button type='button' className={styles.ctaButton} onClick={handleOpenCashier}>
                GO TO CASHIER
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Profile side panel — SIBLING of the modal (not a child) so it isn't
          trapped inside the modal's z-index stacking context. The Modal
          above is demoted to zIndex 900 while AccountMenuPanel sits at its
          default ~950, so the drawer renders ON TOP of the modal and the
          location warning stays visible in the uncovered space around it. */}
      <AccountMenuPanel open={showProfileMenu} onClose={handleCloseProfile} />
    </>
  );
};

export default InvalidLocationModal;
